import '@u-elements/u-details';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { Chevron } from '../';
import type { TrackingProp } from '../types';
import { animateHidden, getText, useTracking } from '../utils';
import styles from './expandable.module.css';
import clsx from 'clsx';

export type ExpandableProps = Omit<
  React.ComponentPropsWithoutRef<'details'>,
  'onChange'
> & {
  defaultOpen?: boolean;
  onChange?: (open: boolean) => void;
  open?: boolean;
  variant?:
    | 'blue-yellow'
    | 'blue-blue'
    | 'blue-white'
    | 'blue' // Backwards compatibility, but is corrected to line-white-white with RegExp
    | 'white-yellow'
    | 'white-white'
    | 'white' // Backwards compatibility, but is corrected to line-white-white with RegExp
    | 'line-white-yellow'
    | 'line-white-white'
    | 'line-white' // Backwards compatibility, but is corrected to line-white-white with RegExp
    | 'line-yellow-yellow'
    | 'line-blue-yellow';
  tracking?: TrackingProp;
};
export type ExpandableContentProps = React.ComponentPropsWithoutRef<'div'>;
export type ExpandableTitleProps = React.ComponentPropsWithoutRef<'summary'>;

const ExpandableComp = forwardRef<HTMLDetailsElement, ExpandableProps>(
  function Expandable(
    {
      className,
      variant: variantRaw = 'blue-yellow',
      defaultOpen: uncontrolled = false,
      onChange,
      open: controlled,
      tracking = true,
      ...rest
    },
    ref
  ) {
    // Support both open (controlled) and defaultOpen (uncontrolled)
    // Read more on https://react.dev/reference/react-dom/components/input#optimizing-re-rendering-on-every-keystroke
    const [defaultOpen, setDefaultOpen] = useState(uncontrolled);
    const open = controlled ?? defaultOpen;
    const prevOpen = useRef(open);
    const innerRef = useRef<HTMLDetailsElement>(null);
    const variant = variantRaw.replace(/^(line-)?([^-]+)$/, '$1$2-white'); // Ensure variant always ends with -white if only one color is provided
    const track = useTracking(tracking);

    useImperativeHandle(ref, () => innerRef.current as HTMLDetailsElement); // Forward ref
    useEffect(() => {
      innerRef.current?.toggleAttribute('open', open); // Sync <details> with state
      if (prevOpen.current === open) return; // Only track and animate on change

      const self = innerRef.current;
      const content = self?.querySelector<HTMLElement>(`.${styles.content}`);
      prevOpen.current = open; // Store state

      track({
        eventCategory: 'expandable',
        eventAction: open ? 'open' : 'close',
        eventLabel: getText(self?.querySelector(`.${styles.title}`))
      });
      if (self && content && (self.open = true))
        animateHidden(content, !open).then(() => (self.open = open));
    });

    const onClick = (event: React.MouseEvent<HTMLDetailsElement>) => {
      const title = event.currentTarget?.querySelector(`.${styles.title}`);
      if (!title?.contains(event.target as Node)) return; // Only trigger on title click
      setDefaultOpen(!open);
      onChange?.(!open);
    };

    return (
      <u-details
        {...rest}
        class={clsx(
          variant.startsWith('line') && styles.line,
          styles.expandable,
          styles[variant],
          className
        )}
        onClick={onClick}
        open={open ? ('' as unknown as boolean) : undefined} // Force React to render open as boolean attribute
        ref={innerRef}
      />
    );
  }
);

export const Expandable = Object.assign(ExpandableComp, {
  Title: forwardRef<HTMLElement, ExpandableTitleProps>(function ExpandableTitle(
    { children, className, ...rest },
    ref
  ) {
    return (
      <u-summary {...rest} class={clsx(styles.title, className)} ref={ref}>
        {children}
        <Chevron aria-hidden className={styles.chevron} />
      </u-summary>
    );
  }),
  Content: forwardRef<HTMLDivElement, ExpandableContentProps>(
    function ExpandableContent({ className, ...rest }, ref) {
      return (
        <div {...rest} className={clsx(styles.content, className)} ref={ref} />
      );
    }
  )
});
